// Platform-owner shortcut — queue a Firefly ask mismatch to dev sync.

import { bugReportApiFetch } from "@/components/common/bugReportClient";
import { buildBugReportPayload } from "@/components/common/bugReportPayload";
import type { AuthContextValue } from "@/contexts/AuthContext";
import type { PrimaryMode } from "@/components/lead/sections/LeadWorkspaceHeaderAccess";
import type { FireflyAskResolution } from "./tourTypes";

export function canFlagFireflyAskMismatch(auth: AuthContextValue): boolean {
  if (auth.status !== "ready") return false;
  const role = auth.platformRole;
  return role === "platform_owner" || role === "platform_dev";
}

function describeResolution(resolution: FireflyAskResolution): string {
  if (resolution.kind === "answer") {
    return `artifact=${resolution.artifactId}; navigate=${resolution.navigateTo?.mode ?? "none"}; highlight=${resolution.highlightTarget ?? "none"}`;
  }
  if (resolution.kind === "denied") {
    return `denied artifact=${resolution.artifactId}; targetMode=${resolution.targetMode}`;
  }
  return "not_found";
}

export async function flagFireflyAskToDevSync(input: {
  queryText: string;
  resolution: FireflyAskResolution;
  mode: PrimaryMode;
  auth: AuthContextValue;
  scopedOrganizationId: string | null;
  getToken: () => Promise<string | null>;
}): Promise<{ ok: true } | { ok: false; error: string }> {
  const token = await input.getToken();
  if (!token) return { ok: false, error: "Sign in required." };

  const description = [
    "[Firefly ask mismatch]",
    `Query: ${input.queryText.trim()}`,
    `Outcome: ${describeResolution(input.resolution)}`,
    "",
    "Expected: (describe what Firefly should have done)",
  ].join("\n");

  const { payload } = buildBugReportPayload({
    description,
    mode: input.mode,
    // Placement is for dev-sync graphic assets only (public/ or src/assets/);
    // source paths fail server validation and block the queue insert.
    repoPath: "",
    auth: input.auth,
    scopedOrganizationId: input.scopedOrganizationId,
    queueForDevSync: true,
  });

  const result = await bugReportApiFetch<{ dev_note_id?: string }>(
    "/api/bugs/report",
    "POST",
    token,
    payload,
  );
  if (!result.ok) return { ok: false, error: result.error };
  return { ok: true };
}
